import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  ClipboardCheck,
  Download,
  MoreHorizontal,
  Pause,
  Play,
  Upload,
} from 'lucide-react';
import type { BespokeTemplateResponse } from '../../services/api/types.gen';
import { countBespokeBulkTargets } from './bulkTemplateStatus';
import type { FormSchemaVersionPin } from './formSchemaUtils';

export function FormSchemasHeader({
  catalogLoading,
  catalogEmpty,
  templates,
  onImport,
  onExport,
  onOpenValidation,
  onOpenBulk,
}: {
  catalogLoading: boolean;
  catalogEmpty: boolean;
  templates: BespokeTemplateResponse[];
  onImport: () => void;
  onExport: () => void;
  onOpenValidation: () => void;
  onOpenBulk: (
    direction: 'pause' | 'resume',
    scopeSummary: string,
    pinsFilter: FormSchemaVersionPin[] | null
  ) => void;
}) {
  const actionsDisabled = catalogLoading || catalogEmpty;

  return (
    <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
      <div>
        <h1 className='text-3xl font-bold'>Forms Manager</h1>
        <p className='text-muted-foreground'>
          Browse schema versions and preview, edit, and validate each
          section&apos;s form layout.
        </p>
      </div>
      <div className='flex flex-wrap gap-2 shrink-0 self-start'>
        <Button
          type='button'
          variant='outline'
          className='shrink-0'
          disabled={actionsDisabled}
          onClick={onImport}
        >
          <Upload className='h-4 w-4 mr-2' aria-hidden />
          Import…
        </Button>
        <Button
          type='button'
          variant='outline'
          className='shrink-0'
          disabled={actionsDisabled}
          onClick={onExport}
        >
          <Download className='h-4 w-4 mr-2' aria-hidden />
          Export…
        </Button>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type='button'
              variant='outline'
              className='h-8 w-8 shrink-0 p-0 [&_svg]:h-4 [&_svg]:w-4'
              disabled={actionsDisabled}
              aria-label='Bulk rendering for entire catalog'
            >
              <MoreHorizontal className='h-4 w-4' aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='end'
            className='w-[min(100vw-2rem,22rem)]'
          >
            <DropdownMenuItem onClick={onOpenValidation}>
              <ClipboardCheck className='h-3.5 w-3.5 mr-2' aria-hidden />
              Validation Results…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={
                catalogLoading ||
                countBespokeBulkTargets(templates, 'pause', null) === 0
              }
              onClick={() =>
                onOpenBulk(
                  'pause',
                  'All bespoke templates in this environment.',
                  null
                )
              }
            >
              <Pause className='h-3.5 w-3.5 mr-2' aria-hidden />
              Pause all Live (entire catalog)
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={
                catalogLoading ||
                countBespokeBulkTargets(templates, 'resume', null) === 0
              }
              onClick={() =>
                onOpenBulk(
                  'resume',
                  'All bespoke templates in this environment.',
                  null
                )
              }
            >
              <Play className='h-3.5 w-3.5 mr-2' aria-hidden />
              Resume all Paused (entire catalog)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
